import {
  Card,
  ChipMetadata,
  IconWithText,
  Link,
  LinkButton,
  Tooltip,
} from '@pmi/dsm-react';
import {
  AlertBlockOutlineIcon,
  CalendarEventOutlineIcon,
  CalendarStartDateOutlineIcon,
  ConnectionAddOutlineIcon,
  ConnectionRemoveConnectionRemoveIcon,
  DotSolidIcon,
  EditOutlineIcon,
  EnvelopeOutlineIcon,
  IdOutlineIcon,
  LocationOutlineIcon,
  PhoneOutlineIcon,
} from '@pmi/dsm-react/dist/components/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import DropDownMenu from '../../../../../shared/components/DropDownMenu';
import {
  getTextForNoPersonId,
  toShortLocaleDateString,
} from '../../../../../shared/helpers';
import useBreakpoint, {
  SIZE_SM,
  SIZE_XS,
} from '../../../../../shared/hooks/useBreakpoint';
import usePermissions from '../../../../../shared/hooks/usePermissions';
import {
  User,
  User2FAPhoneNumberModel,
} from '../../../../../shared/models/UserModels';
import PERMISSIONS from '../../../../../shared/permissions';
import { accountSelector } from '../../../../../shared/store/shared/accountSlice';
import { useGetDealsListQuery } from '../../../../../shared/store/shared/dealsApiSlice';
import EditPhoneNumberModal from '../EditModal/EditUserPhoneNumberModal';
import { useGetPhoneNumberFieldSchemaQuery } from '../EditModal/EditUserPhoneNumberModal/apiSlice';
import DeleteUserModal from './DeleteUserModal';
import UpdatePmiIdModal from './UpdatePmiIdModal';
import ChangeEmailAddressModal from './ChangeEmailAddressModal';
import styles from './userDetails.module.scss';

export interface UserDetailsCardProps {
  user: User;
  hasEnrollments: boolean;
  onDisable: (user: User) => void;
  onCancelViewModal: () => void;
  onRestore: (user: User) => void;
  hasRedemption: boolean;
}

export default function UserDetailsCard({
  user,
  hasEnrollments,
  onDisable,
  onCancelViewModal,
  onRestore,
  hasRedemption,
}: UserDetailsCardProps) {
  const {
    data: phoneNumberFormHelper,
    isLoading: schemaLoading,
    isFetching: schemaFetching,
  } = useGetPhoneNumberFieldSchemaQuery();
  const {
    hasAccess,
    isLoading: isPermissionLoading,
    hasAccountPermissions,
    restrictedAgreementIds,
  } = usePermissions();

  const { currentAccountId } = useSelector(accountSelector);
  const {
    data: currentUserDeals,
    isLoading: queryLoading,
    isFetching,
  } = useGetDealsListQuery(currentAccountId);
  const accessibleDeals = useMemo(() => {
    const hasPermission = hasAccess({
      permissions: [PERMISSIONS.ManageDealUsers],
    });
    return hasPermission ? currentUserDeals ?? [] : [];
  }, [queryLoading, isFetching, currentUserDeals]);

  const [showEditPhoneNumberModal, setShowEditPhoneNumberModal] =
    useState(false);

  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [showUpdatePmiIdModal, setShowUpdatePmiIdModal] = useState(false);
  const [showChangeEmailAddressModal, setShowChangeEmailAddressModal] =
    useState(false);

  const [showMenu, setShowMenu] = useState(false);
  const menuRef: any = useRef(undefined);
  const menuContainerRef: any = useRef(undefined);
  const bp = useBreakpoint();
  const hideIcon = bp === SIZE_XS || bp === SIZE_SM;

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      // Bypass when clicked on the menu container
      if (
        menuContainerRef?.current &&
        menuContainerRef.current.contains(event.target)
      ) {
        return;
      }

      if (menuRef?.current && !menuRef?.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  const handleMenuClick = (e: any) => {
    // Bypass when clicked inside the dropdown menu
    if (menuRef?.current && menuRef?.current.contains(e.target)) {
      return;
    }

    setShowMenu(!showMenu);
  };

  if (!user) return null;

  const matchedRestore = useMemo(
    () =>
      user.deals.filter((deal) =>
        accessibleDeals?.find(
          (ad: { id: string }) =>
            ad.id === deal.dealId &&
            hasAccess({ permissions: [PERMISSIONS.RestoreDealUser] }),
        ),
      ),
    [accessibleDeals, user],
  );
  const canRestoreUser =
    (hasAccountPermissions() &&
      hasAccess({ permissions: [PERMISSIONS.InviteAdminOrReporter] })) ||
    user.deals?.length === matchedRestore?.length;

  const hasRestrictedProducts = useMemo(
    () =>
      user.assignedProducts.length > 0 &&
      user.deals.some((d) => (restrictedAgreementIds ?? []).includes(d.dealId)),
    [user, restrictedAgreementIds],
  );

  const hasConsumedEntitlement = hasEnrollments || hasRedemption;

  const isDisableBlockedByEntitlement =
    hasEnrollments || (!user.isRemoved && hasRedemption);

  const isDisableUserBlocked =
    !canRestoreUser || isDisableBlockedByEntitlement || hasRestrictedProducts;

  const isDeleteUserBlocked = !user.isRemoved || hasConsumedEntitlement;

  const isUpdatePmiIdBlocked =
    !user.isRemoved ||
    hasConsumedEntitlement ||
    user.invitationStatus === 'Pending';

  const isInternalAdmin = hasAccess({
    permissions: [PERMISSIONS.ManageElevatedAdmins],
  });

  const isAdmin = hasAccess({
    permissions: [PERMISSIONS.ManageDealUsers],
  });

  // Reusable tooltip message builder
  const cannotPrefix = 'You cannot ';
  const enrollmentBlockSuffix =
    'a user if they have enrollments against the Account. Please refund the orders and try again.';

  const getEnrollmentBlockMessage = (action: string) =>
    `${cannotPrefix}${action} ${enrollmentBlockSuffix}`;

  const pmiIdTooltipMessage = useMemo(() => {
    if (hasConsumedEntitlement) {
      return getEnrollmentBlockMessage('edit the PMI ID of');
    }
    if (user.invitationStatus === 'Pending') {
      return `${cannotPrefix}edit the PMI ID of a pending user. They must be registered first.`;
    }
    return `${cannotPrefix}edit the PMI ID of an Enabled user. Please disable the user and try again.`;
  }, [hasConsumedEntitlement, user.invitationStatus]);

  return (
    <Card variant="stroke" className={`${styles.userDetailsContainer}`}>
      <div>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="d-flex align-items-center flex-wrap">
            <h6 className="line-height-0 right-divider fw-bold" data-exclude-lg>
              {user.personName}
              {user.isRemoved && (
                <ChipMetadata
                  text="Disabled"
                  variant="red"
                  className="ms-1 chipmetadata-pill"
                  icon={<DotSolidIcon className="txt-8" />}
                />
              )}
            </h6>
            <div
              className={`d-flex align-items-center flex-wrap ${styles.customContainer}`}
            >
              {isInternalAdmin && (
                <div
                  id="viewUserModal-personId"
                  className="d-flex align-items-center flex-wrap"
                >
                  <span className={`ms-1 ${styles.textSecondary}`}>
                    PMI ID:{' '}
                  </span>
                  <strong
                    className={`ms-1 right-divider ${styles.textSecondary}`}
                  >
                    {user.personId
                      ? user.personId
                      : getTextForNoPersonId(user, true)}
                  </strong>
                </div>
              )}
              <div
                className={`d-flex flex-wrap flex-wrap ${styles.customContainer}`}
              >
                {!!user.createdDate && (
                  <div
                    id="viewUserModal-invitedOn"
                    className="d-flex align-items-center"
                  >
                    <IconWithText
                      icon={hideIcon ? null : <CalendarEventOutlineIcon />}
                      text="Invited on:"
                      className={`${styles.customIconWithText}  ${
                        hideIcon ? 'ms-1' : ''
                      }`}
                    />
                    <strong
                      className={`ms-1 right-divider ${styles.textSecondary}`}
                    >
                      {toShortLocaleDateString(user.createdDate)}
                    </strong>
                  </div>
                )}

                <div
                  id="viewUserModal-joinedOn"
                  className="d-flex align-items-center flex-wrap"
                >
                  <IconWithText
                    icon={hideIcon ? null : <CalendarStartDateOutlineIcon />}
                    text="Joined on:"
                    className={`${styles.customIconWithText}  ${
                      hideIcon ? 'ms-1' : ''
                    }`}
                  />

                  <strong className={`ms-1 ${styles.textSecondary}`}>
                    {user.invitationStatus?.toLowerCase() === 'pending'
                      ? 'Not joined'
                      : toShortLocaleDateString(user.dateRegistered)}
                  </strong>
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-end">
            <LinkButton
              className="view-user-edit-button mb-md-0 mb-2"
              size="md"
              titleText="Edit"
              variant="light"
              icon={EditOutlineIcon}
              disabled={isPermissionLoading}
              onClick={(e) => {
                if (!isPermissionLoading) {
                  handleMenuClick(e);
                }
              }}
            />
            <DropDownMenu
              showMenu={showMenu}
              menuRef={menuRef}
              menuClassName="view-user-edit-menu"
              hideIcon
            >
              <div className="dsm bg-white rounded shadow overflow-hidden">
                <div className="org-selector-menu py-2 px-3">
                  {isInternalAdmin && (
                    <LinkButton
                      size="sm"
                      className="p-0 m-0 text-black fw-normal txt-14 text-start text-nowrap"
                      icon={EnvelopeOutlineIcon}
                      titleText="Change Email Address"
                      onClick={(e: any) => {
                        e.preventDefault();
                        setShowChangeEmailAddressModal(true);
                      }}
                    />
                  )}
                  <LinkButton
                    size="sm"
                    className="p-0 m-0 text-black fw-normal txt-14"
                    disabled={
                      schemaLoading ||
                      schemaFetching ||
                      user.isRemoved ||
                      (!isAdmin && !isInternalAdmin)
                    }
                    icon={PhoneOutlineIcon}
                    titleText={`${
                      user.phoneNumber
                        ? 'Edit Phone Number'
                        : 'Add Phone number'
                    }`}
                    onClick={(e: any) => {
                      e.preventDefault();
                      if (schemaLoading || schemaFetching) return;
                      setShowEditPhoneNumberModal(true);
                    }}
                  />

                  <Tooltip
                    content={(() => {
                      if (hasRestrictedProducts)
                        return 'You cannot disable this user. Please contact PartnerSupport@pmi.org.';
                      if (isDisableBlockedByEntitlement)
                        return getEnrollmentBlockMessage(
                          user.isRemoved ? 'enable' : 'disable',
                        );
                      return `${cannotPrefix}disable the user for this Account. Please contact PartnerSupport@pmi.org.`;
                    })()}
                    type="sm"
                    blockTriggers={!isDisableUserBlocked}
                    placement="left"
                    className="darkTooltip"
                  >
                    <LinkButton
                      size="sm"
                      className="p-0 m-0 text-black fw-normal"
                      icon={AlertBlockOutlineIcon}
                      titleText={`${
                        user.isRemoved ? 'Enable User' : 'Disable User'
                      }`}
                      disabled={
                        isDisableUserBlocked || (!isAdmin && !isInternalAdmin)
                      }
                      onClick={() => {
                        if (isDisableUserBlocked) return;
                        if (user.isRemoved) {
                          onRestore(user);
                          return;
                        }
                        onDisable(user);
                      }}
                    />
                  </Tooltip>

                  {isInternalAdmin && (
                    <>
                      <Tooltip
                        content={
                          hasConsumedEntitlement
                            ? getEnrollmentBlockMessage('delete')
                            : `${cannotPrefix}delete an enabled user. Please disable the user and try again.`
                        }
                        type="sm"
                        blockTriggers={!isDeleteUserBlocked}
                        placement="left"
                        className="darkTooltip"
                      >
                        <LinkButton
                          size="sm"
                          className="p-0 m-0 text-danger fw-normal"
                          icon={ConnectionRemoveConnectionRemoveIcon}
                          titleText="Delete User"
                          disabled={isDeleteUserBlocked}
                          onClick={(e: any) => {
                            e.preventDefault();
                            if (isDeleteUserBlocked) return;
                            setShowDeleteUserModal(true);
                          }}
                        />
                      </Tooltip>
                      <Tooltip
                        content={pmiIdTooltipMessage}
                        type="sm"
                        blockTriggers={!isUpdatePmiIdBlocked}
                        placement="left"
                        className="darkTooltip"
                      >
                        <LinkButton
                          size="sm"
                          className="p-0 m-0 text-black fw-normal"
                          icon={IdOutlineIcon}
                          titleText="Update PMI ID"
                          disabled={isUpdatePmiIdBlocked}
                          onClick={(e: any) => {
                            e.preventDefault();
                            if (isUpdatePmiIdBlocked) return;
                            setShowUpdatePmiIdModal(true);
                          }}
                        />
                      </Tooltip>
                    </>
                  )}
                </div>
              </div>
            </DropDownMenu>
          </div>
        </div>
        <hr className={styles.divider} />
        <div className="d-flex flex-wrap">
          <div className="d-flex flex-column align-items-flex-start">
            {!!user.email && (
              <div
                id="viewUserModal-email"
                className="d-flex align-items-center mb-2 flex-wrap me-5"
                data-exclude-lg
              >
                <IconWithText
                  icon={<EnvelopeOutlineIcon />}
                  text="Email:"
                  className={styles.customIconWithText}
                />
                <Link
                  label={user.email}
                  href={`mailto:${user.email}`}
                  className={`ms-1 ${styles.monochrome} ${styles.textSecondary}`}
                />
              </div>
            )}

            <div
              id="viewUserModal-phone"
              className="d-flex align-items-center mb-2 flex-wrap"
              data-exclude-lg
            >
              <IconWithText
                icon={<PhoneOutlineIcon />}
                text="Phone Number: "
                className={styles.customIconWithText}
              />
              <span className={`ms-1 ${styles.textSecondary}`}>
                {user.phoneNumber && user.phoneNumber.toString().length > 0
                  ? `+${user.phoneCountryCode}${user.phoneNumber}`
                  : '-'}
              </span>
            </div>
          </div>
          <div className="d-flex flex-column">
            {!!user.createdByPersonFullName && (
              <div
                id="viewUserModal-createdBy"
                className="d-flex align-items-center mb-2 flex-wrap"
                data-exclude-lg
              >
                <IconWithText
                  icon={<ConnectionAddOutlineIcon />}
                  text="Invited by:"
                  className={styles.customIconWithText}
                />
                <strong className={`ms - 1 ${styles.textSecondary}`}>
                  &nbsp;{user.createdByPersonFullName}
                </strong>
              </div>
            )}
            <div
              id="viewUserModal-region"
              className="d-flex align-items-center mb-2 flex-wrap"
            >
              <IconWithText
                icon={<LocationOutlineIcon />}
                text="Registration Country / Region: "
                className={styles.customIconWithText}
              />
              <strong className={`ms-1 ${styles.textSecondary}`}>
                {user.countryCode || '-'}
              </strong>
            </div>
          </div>
        </div>
      </div>

      {showDeleteUserModal && (
        <DeleteUserModal
          user={user}
          onCancel={() => setShowDeleteUserModal(false)}
          onSuccess={onCancelViewModal}
        />
      )}

      {showUpdatePmiIdModal && (
        <UpdatePmiIdModal
          user={user}
          onCancel={() => setShowUpdatePmiIdModal(false)}
        />
      )}

      {showChangeEmailAddressModal && (
        <ChangeEmailAddressModal
          user={user}
          onCancel={() => setShowChangeEmailAddressModal(false)}
        />
      )}

      {showEditPhoneNumberModal && (
        <EditPhoneNumberModal
          accountUserId={user.id}
          countries={phoneNumberFormHelper?.countries}
          phoneNumberSchema={phoneNumberFormHelper?.schema}
          phoneNumber={
            {
              number: user.phoneNumber,
              countryCode: user.phoneCountryCode,
              countryCodeId: user.phoneCountryCodeId,
            } as User2FAPhoneNumberModel
          }
          onCancel={() => setShowEditPhoneNumberModal(false)}
        />
      )}
    </Card>
  );
}
